import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Divider, 
  Paper, 
  Skeleton,
  Button,
  Avatar,
  Card,
  CardHeader,
  CardContent,
  Grid as MuiGrid
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { productsApi, categoriesApi } from '../api/api';
import ProductCard from './ProductCard';

// Create a properly typed Grid component wrapper
const Grid = React.memo(MuiGrid);

// Shared featured collection data - this should match the one in HomePage.tsx
export const FEATURED_CATEGORIES = [
  {
    id: 177597826,
    name: 'Made in Canada',
    description: 'Products proudly made in Canada',
  },
  {
    id: 142312356,
    name: 'New Items and Specials',
    description: 'Our newest products and special offers',
  }
];

const FeaturedCollections: React.FC = () => {
  // Get products for first collection (Made in Canada)
  const { data: canadaProducts, isLoading: loadingCanada } = useQuery({
    queryKey: ['collection-products', FEATURED_CATEGORIES[0].id],
    queryFn: () => productsApi.getProductsByCategory(FEATURED_CATEGORIES[0].id, { limit: 4, inStock: true }),
    staleTime: 300000, // 5 minutes
  });

  // Get more products for second collection to account for filtering
  const { data: newItemsProductsRaw, isLoading: loadingNewItems } = useQuery({
    queryKey: ['collection-products', FEATURED_CATEGORIES[1].id],
    queryFn: () => productsApi.getProductsByCategory(FEATURED_CATEGORIES[1].id, { limit: 8, inStock: true }),
    staleTime: 300000, // 5 minutes
  });

  // Filter new items to exclude products that are already in Made in Canada
  const newItemsProducts = React.useMemo(() => {
    if (!newItemsProductsRaw?.items || !canadaProducts?.items) {
      return newItemsProductsRaw;
    }

    const canadaProductIds = new Set(canadaProducts.items.map(product => product.id));
    const filteredItems = newItemsProductsRaw.items.filter(product => !canadaProductIds.has(product.id));
    
    return {
      ...newItemsProductsRaw,
      items: filteredItems.slice(0, 4) // Take only 4 after filtering
    };
  }, [newItemsProductsRaw, canadaProducts]);

  // Get category info with thumbnails
  const { data: categoryData1 } = useQuery({
    queryKey: ['category-data', FEATURED_CATEGORIES[0].id],
    queryFn: () => categoriesApi.getCategory(FEATURED_CATEGORIES[0].id),
    staleTime: 300000,
  });

  const { data: categoryData2 } = useQuery({
    queryKey: ['category-data', FEATURED_CATEGORIES[1].id],
    queryFn: () => categoriesApi.getCategory(FEATURED_CATEGORIES[1].id),
    staleTime: 300000,
  });

  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
        Featured Collections
      </Typography>
      <Divider sx={{ mb: 4 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
        {FEATURED_CATEGORIES.map((collection, index) => {
          const isLoadingCollection = index === 0 ? loadingCanada : loadingNewItems;
          const collectionProducts = index === 0 ? canadaProducts?.items : newItemsProducts?.items;
          const categoryData = index === 0 ? categoryData1 : categoryData2;
          const thumbnailUrl = categoryData?.thumbnailUrl || categoryData?.imageUrl;
          
          return (
            <Box 
              key={collection.id}
              sx={{ 
                width: { xs: '100%', md: '50%' }, 
                px: 2, 
                mb: { xs: 4, md: 0 }
              }}
            >
              <Card elevation={0} sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <CardHeader
                  avatar={
                    thumbnailUrl ? (
                      <Avatar 
                        src={thumbnailUrl} 
                        alt={collection.name}
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Skeleton variant="circular" width={48} height={48} />
                    )
                  }
                  title={
                    <Typography variant="h6" component="h3" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                      {collection.name}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {collection.description}
                    </Typography>
                  }
                  action={
                    <Button 
                      component={RouterLink} 
                      to={`/category/${collection.id}`}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        fontWeight: 'medium',
                        textTransform: 'none',
                        fontSize: '0.85rem'
                      }}
                    >
                      View All
                    </Button>
                  }
                  sx={{ 
                    pb: 1,
                    pt: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                />
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                  {isLoadingCollection ? (
                    // Loading skeleton
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: { 
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 1.5
                    }}>
                      {Array.from(new Array(4)).map((_, idx) => (
                        <Box key={idx}>
                          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
                          <Skeleton variant="text" sx={{ mt: 0.5 }} />
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="40%" height={36} sx={{ mt: 1 }} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    // Products display
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: { 
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 1.5
                    }}>
                      {collectionProducts?.map(product => (
                        <Box key={product.id} sx={{ width: '100%', transform: 'scale(0.95)', transformOrigin: 'top left' }}>
                          <ProductCard product={product} />
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default FeaturedCollections; 